import {
  AdditiveBlending, CanvasTexture, Color, Group, Sprite, SpriteMaterial, Vector3,
} from 'three';
import { currentSpawnRadius, heightColor, sphereHeight, sphereIsMobile } from './LiquidSphere';

// ═══════════════════════════════════════════════════════════════════════════════════════════
// 3 petites llums bioluminescents flotant DINS de l'esfera de partícules (2026-10-03, retocat
// 2026-10-04) — sistema SEPARAT i lleuger, LiquidSphere no en sap res i no canvia. Reutilitza
// spawnRadius i la interpolació de color (heightColor) de LiquidSphere en lloc de duplicar-los.
//
// Render: 2 sprites additius per lluerna (nucli petit+brillant, halo gran+tou — mateixa textura
// radial reutilitzada, escalada diferent), + registrades a una LAYER pròpia (FIREFLY_LAYER, veure
// Scene.js) inclosa al sistema de bloom EXISTENT del projecte (SceneCompositor, per layer) com a
// MILLORA ADDICIONAL quan s'activa (Scene · Render → Bloom). 2026-10-05 fix: el halo NO pot
// dependre NOMÉS del bloom — és un toggle GLOBAL, off per defecte, que afecta tota l'escena; sense
// un halo propi al sprite, sense activar-lo les lluernes només es veien com el nucli (un puntet).
// Ara es veuen com a llum SEMPRE; el bloom (si s'activa) hi afegeix un glow addicional per sobre.
// NO THREE.PointLight reals — il·luminar ~15000 partícules amb llums dinàmiques forçaria
// recompilar el material instanciat per un efecte que ja tenim gratis amb sprites.
//
// Moviment: "steering" (impuls → velocitat → posició — mateix estil que el GPGPU del ratolí, aquí
// en JS pla perquè només són 3 objectes) — MAI cercles/el·lipses/sinusoides de posició ni bot
// contra la vora. DUES capes independents (2026-10-04 fix: abans no existia la segona):
//   A) Moviment PRINCIPAL — determina quant viatgen: `speed` (velocitat màxima real),
//      `steering` (com de ràpid la velocitat persegueix la direcció objectiu) i `drift` (quant
//      "tremola" aleatòriament aquesta direcció objectiu, mai periòdic). Vora suau: per sobre de
//      spawnRadius×safetyRadius apareix una força (creixent, smoothstep) cap al centre, SUMADA al
//      steering — mai substitueix la velocitat ni la inverteix. La velocitat només canvia per
//      acceleració, mai s'assigna directament → canvis de rumb sempre graduals.
//   B) Floating SECUNDARI — `floatAmplitude`/`floatSpeed`: un bombolleig orgànic petit (3
//      sinusoides, fase/freqüència pròpia per lluerna) que es SUMA a la posició NOMÉS en pintar
//      l'sprite — mai retroalimenta velocity/steering, així que MAI afecta la velocitat de viatge
//      real (abans no hi havia cap capa separada per això).
// ═══════════════════════════════════════════════════════════════════════════════════════════

const COUNT = 3;

export const FIREFLY_SETTINGS_DESKTOP = {
  size: 0.003,             // SCALE · radi del NUCLI brillant de cada lluerna (petit a propòsit)
  haloSize: 6,              // NONE · mida del halo propi = size × això (sempre visible, no depèn del bloom)
  glowIntensity: 2.55,      // NONE · brillantor del nucli I intensitat del halo (propi + bloom si s'activa)

  // ── A) Moviment principal — determina QUANT viatgen ──
  speed: 0.07,             // SCALE (× HEIGHT/s) · velocitat màxima de viatge real
  steering: 1.2,           // NONE (1/s) · com de ràpid giren cap a la direcció objectiu
  drift: 0.6,              // NONE · aleatorietat orgànica de la direcció objectiu (mai periòdic)

  // ── Vora: zona de viratge suau, no una paret ──
  safetyRadius: 0.65,      // NONE · fracció de spawnRadius on comencen a virar
  boundaryForce: 6.7,      // NONE · força cap al centre a la vora (creix amb smoothstep)

  // ── B) Floating secundari — bombolleig orgànic, NO afecta la velocitat de viatge ──
  floatAmplitude: 0.05,    // SCALE · amplitud del bombolleig petit al voltant del trajecte
  floatSpeed: 0.15,        // NONE · velocitat d'aquest bombolleig
};

// Mobile: l'esfera és ~2.5× més petita respecte a HEIGHT (spawnRadius 0.26 vs 0.65) → mida, velocitat i
// bombolleig escalats en proporció perquè no la creuïn en un instant ni la tapin.
export const FIREFLY_SETTINGS_MOBILE = {
  ...FIREFLY_SETTINGS_DESKTOP,
  size: 0.00285,          // mida, velocitat i bombolleig × radi de l'esfera (0.26 → 0.22 = ×0.85)
  speed: 0.024,
  floatAmplitude: 0.017,
  drift: 0.8,             // més viratge orgànic: en un espai més petit, menys trajectes rectes contra la vora
};

export const FIREFLY_SETTINGS = { ...FIREFLY_SETTINGS_DESKTOP };

// Com LIQUID_SETTINGS: mobile es decideix en crear l'esfera (sphereIsMobile), un sol cop.
let _platformApplied = false;

const _tmpDesired = new Vector3();
const _tmpSteer = new Vector3();
const _tmpToCenter = new Vector3();
const _tmpFloat = new Vector3();
const _tmpRenderPos = new Vector3();

// Mateixa textura per al nucli (escala petita) i el halo (escala gran, opacitat baixa) — el
// gradient prou suau perquè funcioni bé a totes dues escales sense necessitar dues textures.
function buildGlowTexture() {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.12, 'rgba(255,255,255,0.95)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.35)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  return new CanvasTexture(canvas);
}

// Punt uniforme sobre l'ESFERA unitat (Marsaglia) — no un cub normalitzat, que esbiaixa cap als
// vèrtexs.
function randomUnitVector(target = new Vector3()) {
  let x, y, z, d2;
  do {
    x = Math.random() * 2 - 1;
    y = Math.random() * 2 - 1;
    z = Math.random() * 2 - 1;
    d2 = x * x + y * y + z * z;
  } while (d2 > 1 || d2 < 1e-6);
  return target.set(x, y, z).normalize();
}

class Firefly {
  constructor(spawnRadiusPx) {
    // Neix ben dins de la zona segura (uniforme en volum, com basePosition a LiquidSphere).
    this.position = randomUnitVector().multiplyScalar(spawnRadiusPx * 0.4 * Math.cbrt(Math.random()));
    this.velocity = randomUnitVector().multiplyScalar(spawnRadiusPx * 0.01);
    this.wanderDir = randomUnitVector();
    // Petites variacions per lluerna perquè MAI es moguin sincronitzades ni idèntiques.
    this.speedJitter = 0.75 + Math.random() * 0.5;
    this.driftJitter = 0.75 + Math.random() * 0.5;
    this.floatPhase = new Vector3(Math.random(), Math.random(), Math.random()).multiplyScalar(Math.PI * 2);
    this.floatFreq = new Vector3(0.6 + Math.random() * 0.6, 0.6 + Math.random() * 0.6, 0.6 + Math.random() * 0.6);
    this.pulsePhase = Math.random() * Math.PI * 2;
    this.pulseFreq = 0.5 + Math.random() * 0.5;
    this.color = new Color();
  }
}

export default class Fireflies {
  constructor() {
    this.group = null;
    this.cores = [];
    this.halos = [];
    this.items = [];
    this._texture = null;
    this._time = 0;
  }

  init() {
    if (!_platformApplied) {
      _platformApplied = true;
      if (sphereIsMobile()) Object.assign(FIREFLY_SETTINGS, FIREFLY_SETTINGS_MOBILE);
    }
    this.group = new Group();
    this._texture = buildGlowTexture();

    const makeSprite = (opacity) => new Sprite(new SpriteMaterial({
      map: this._texture,
      color: 0xffffff,
      opacity,
      blending: AdditiveBlending,
      transparent: true,
      depthWrite: false,
    }));

    for (let i = 0; i < COUNT; i++) {
      this.halos.push(makeSprite(0.55)); // gran i tou, DARRERE del nucli
      this.cores.push(makeSprite(1));    // petit i brillant, A SOBRE
    }
    this.halos.forEach((s) => this.group.add(s));
    this.cores.forEach((s) => this.group.add(s));

    const px = this._computePx();
    this.items = [];
    for (let i = 0; i < COUNT; i++) this.items.push(new Firefly(px.spawnRadius));

    return this.group;
  }

  _computePx() {
    const s = FIREFLY_SETTINGS;
    const H = sphereHeight();
    const spawnRadius = H * currentSpawnRadius();
    return {
      size: H * s.size,
      speed: H * s.speed,
      floatAmplitude: H * s.floatAmplitude,
      spawnRadius,
      safeRadius: spawnRadius * s.safetyRadius,
    };
  }

  loop(dt = 0.016) {
    if (!this.group) return;
    dt = Math.min(dt, 1 / 30);
    this._time += dt;

    const px = this._computePx();
    const s = FIREFLY_SETTINGS;

    for (let i = 0; i < this.items.length; i++) {
      const f = this.items[i];
      this._updateFirefly(f, dt, px, s);

      // Floating secundari: NOMÉS afecta on es DIBUIXA el sprite, mai f.position/f.velocity — per
      // això mai contribueix a la velocitat de viatge real (capa B, independent de la capa A).
      // 2026-10-05 fix: faltava multiplicar per s.floatSpeed — abans NOMÉS depenia de la freqüència
      // pròpia (fixa) de cada lluerna, així que el setting global no feia RES.
      const t = this._time * s.floatSpeed;
      _tmpFloat.set(
        Math.sin(t * f.floatFreq.x * Math.PI * 2 + f.floatPhase.x),
        Math.sin(t * f.floatFreq.y * Math.PI * 2 + f.floatPhase.y),
        Math.sin(t * f.floatFreq.z * Math.PI * 2 + f.floatPhase.z),
      ).multiplyScalar(px.floatAmplitude);
      _tmpRenderPos.copy(f.position).add(_tmpFloat);

      const pulse = 1 + 0.18 * Math.sin(this._time * f.pulseFreq * Math.PI * 2 + f.pulsePhase);
      heightColor(f.position.y, px.spawnRadius, f.color);

      const core = this.cores[i];
      core.position.copy(_tmpRenderPos);
      core.scale.setScalar(Math.max(px.size * pulse, 1e-4));
      core.material.color.copy(f.color).multiplyScalar(s.glowIntensity);

      const halo = this.halos[i];
      halo.position.copy(_tmpRenderPos);
      halo.scale.setScalar(Math.max(px.size * s.haloSize * pulse, 1e-4));
      halo.material.color.copy(f.color).multiplyScalar(s.glowIntensity);
    }
  }

  // Steering pur (capa A, moviment principal): drift (direcció objectiu que deriva sola) + vora
  // suau (força addicional cap al centre, mai una inversió de velocitat) → tot integrat per
  // acceleració (steering), mai assignat directament.
  _updateFirefly(f, dt, px, s) {
    f.wanderDir.x += (Math.random() * 2 - 1) * s.drift * f.driftJitter * dt;
    f.wanderDir.y += (Math.random() * 2 - 1) * s.drift * f.driftJitter * dt;
    f.wanderDir.z += (Math.random() * 2 - 1) * s.drift * f.driftJitter * dt;
    f.wanderDir.normalize();

    const maxSpeed = Math.max(px.speed * f.speedJitter, 1e-5);
    const desired = _tmpDesired.copy(f.wanderDir).multiplyScalar(maxSpeed);
    const steer = _tmpSteer.copy(desired).sub(f.velocity).multiplyScalar(s.steering);

    const dist = f.position.length();
    if (dist > px.safeRadius) {
      const range = Math.max(px.spawnRadius - px.safeRadius, 1e-4);
      const t = Math.min(1, (dist - px.safeRadius) / range);
      const boundaryStrength = t * t * (3 - 2 * t); // smoothstep: creix suau, sense frontera dura
      const toCenter = _tmpToCenter.copy(f.position).multiplyScalar(-1 / Math.max(dist, 1e-4));
      steer.addScaledVector(toCenter, s.boundaryForce * boundaryStrength * maxSpeed);
    }

    f.velocity.addScaledVector(steer, dt);
    const speed = f.velocity.length();
    if (speed > maxSpeed) f.velocity.multiplyScalar(maxSpeed / speed);

    f.position.addScaledVector(f.velocity, dt);
  }

  setLayer(n) {
    if (this.group) this.group.traverse((o) => o.layers.set(n));
  }

  // No hi ha res "estàtic" a reconstruir (sense GPU/atributs) — loop() ja recalcula this._computePx()
  // cada frame, així que un resize real simplement es reflecteix sol al frame següent.
  resize() {}

  dispose() {
    this.cores.forEach((s) => s.material.dispose());
    this.halos.forEach((s) => s.material.dispose());
    this._texture?.dispose();
    this.cores = [];
    this.halos = [];
    this.items = [];
    this._texture = null;
    this.group = null;
  }

  get settings() { return FIREFLY_SETTINGS; }
}
